import { ref, computed } from 'vue'

import type { ChatMessageTemplate } from '@/types/app'
import {
  useAddChatMessageTemplateMutation,
  useDeleteChatMessageTemplateMutation,
  useUpdateChatMessageTemplateMutation,
  useViewerOnUseChatMessageTemplateQuery,
} from './useChatMessageTemplate.generated'
import type { ChatMessageTemplateOnUseChatMessageTemplateFragment } from './useChatMessageTemplate.generated'

export const cloneChatTemplate = (
  data: ChatMessageTemplate
): ChatMessageTemplate => ({
  ...data,
  selections: [...data.selections],
})

export const useChatMessageTemplate = () => {
  const {
    loading: initialLoading,
    result,
    refetch: refetchChatMessageTemplates,
  } = useViewerOnUseChatMessageTemplateQuery({
    fetchPolicy: 'no-cache',
  })

  const chatMessageTemplates = computed(() => {
    if (!result.value) {
      return []
    }

    return result.value.viewer.chatMessageTemplates
  })

  const loading = ref(false)

  const addChatMessageTemplateMutation = useAddChatMessageTemplateMutation({})

  const createTemplate = async (
    label: string,
    body: string,
    selections: string[]
  ): Promise<ChatMessageTemplateOnUseChatMessageTemplateFragment> => {
    loading.value = true

    const res = await addChatMessageTemplateMutation.mutate({
      input: {
        label,
        body,
        selections,
      },
    })

    await refetchChatMessageTemplates()

    loading.value = false

    const addedTemplate = (() => {
      const data = res?.data
      if (!data) {
        throw new Error('Failed to add template')
      }

      // 当時 dolphin の利用者で Array.at を使える利用者が 95% を超えていなかった
      return data.addChatMessageTemplate[data.addChatMessageTemplate.length - 1]
    })()

    return addedTemplate
  }

  const updateChatMessageTemplateMutation =
    useUpdateChatMessageTemplateMutation({})

  const updateTemplate = async (data: ChatMessageTemplate) => {
    loading.value = true

    await updateChatMessageTemplateMutation.mutate({
      input: {
        guid: data.guid,
        label: data.label,
        body: data.body,
        selections: data.selections,
      },
    })

    loading.value = false
  }

  const deleteChatMessageTemplateMutation =
    useDeleteChatMessageTemplateMutation({})

  const deleteTemplate = async (guid: string) => {
    loading.value = true

    await deleteChatMessageTemplateMutation.mutate({
      guid: guid,
    })

    await refetchChatMessageTemplates()

    loading.value = false
  }

  return {
    loading: computed(() => initialLoading.value || loading.value),
    data: computed(() => chatMessageTemplates.value ?? []),
    map: computed(
      () =>
        new Map(
          chatMessageTemplates.value?.map(item => [item.guid, item]) ?? []
        )
    ),
    createTemplate,
    updateTemplate,
    deleteTemplate,
  }
}
